// Localização: frontend/src/context/NotificationContext.jsx
// Contexto para gerenciar notificações toast globalmente

import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext(null);

/**
 * Provider de Notificações
 * Gerencia o estado global das notificações toast
 */
export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [history, setHistory] = useState([]); // Histórico de todas as notificações

    /**
     * Remove uma notificação específica
     */
    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, []);

    /**
     * Adiciona uma nova notificação
     * @param {string} message - Mensagem a ser exibida
     * @param {string} type - Tipo: 'success', 'error', 'warning', 'info'
     * @param {number} duration - Duração em milissegundos (padrão: 5000)
     */
    const showNotification = useCallback((message, type = 'info', duration = 5000) => {
        console.log('[NotificationContext] showNotification chamado:', { message, type, duration });
        const id = Date.now() + Math.random();
        const timestamp = new Date().toISOString();
        const notification = {
            id,
            message,
            type,
            duration,
            timestamp
        };

        setNotifications(prev => {
            console.log('[NotificationContext] Adicionando notificação. Total anterior:', prev.length);
            const newNotifications = [...prev, notification];
            console.log('[NotificationContext] Total após adicionar:', newNotifications.length);
            return newNotifications;
        });

        // Adicionar ao histórico
        setHistory(prev => {
            const newHistory = [notification, ...prev];
            // Manter apenas as últimas 100 notificações no histórico
            return newHistory.slice(0, 100);
        });

        // Remove automaticamente após a duração especificada
        if (duration > 0) {
            setTimeout(() => {
                setNotifications(prev => prev.filter(notif => notif.id !== id));
            }, duration);
        }

        return id;
    }, []);

    /**
     * Remove todas as notificações ativas
     */
    const clearAll = useCallback(() => {
        setNotifications([]);
    }, []);

    /**
     * Limpa o histórico de notificações
     */
    const clearHistory = useCallback(() => {
        setHistory([]);
    }, []);

    /**
     * Remove uma notificação do histórico
     */
    const removeFromHistory = useCallback((id) => {
        setHistory(prev => prev.filter(notif => notif.id !== id));
    }, []);

    // Métodos de conveniência para cada tipo
    const success = useCallback((message, duration) => {
        return showNotification(message, 'success', duration);
    }, [showNotification]);

    const error = useCallback((message, duration) => {
        return showNotification(message, 'error', duration);
    }, [showNotification]);

    const warning = useCallback((message, duration) => {
        return showNotification(message, 'warning', duration);
    }, [showNotification]);

    const info = useCallback((message, duration) => {
        return showNotification(message, 'info', duration);
    }, [showNotification]);

    const value = {
        notifications,
        history,
        showNotification,
        removeNotification,
        clearAll,
        clearHistory,
        removeFromHistory,
        success,
        error,
        warning,
        info
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

/**
 * Hook para usar o contexto de notificações
 */
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification deve ser usado dentro de um NotificationProvider');
    }
    return context;
};

export default NotificationContext;

